import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';

interface Props {
  suggestions: string[];
  onSelect: (suggestion: string) => void;
  currentValue?: string;
  showRemoveOption?: boolean;
}

const Dropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  max-width: 300px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
`;

const SuggestionItem = styled.button<{ isSelected: boolean; isCustom: boolean; isRemove?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => 
    props.isRemove ? `${theme.colors.error}10` :
    props.isSelected ? theme.colors.primaryLight : 
    props.isCustom ? theme.colors.backgroundSecondary : 
    'transparent'};
  border: none;
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  color: ${props => 
    props.isRemove ? theme.colors.error :
    props.isSelected ? theme.colors.primary : 
    theme.colors.text.primary};
  transition: all 0.15s ease;
  font-family: ${theme.fonts.body};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${props => 
      props.isSelected ? theme.colors.primaryLight : 
      theme.colors.backgroundSecondary};
    color: ${props => 
      props.isSelected ? theme.colors.primary : 
      theme.colors.text.primary};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const SuggestionIcon = styled.span`
  margin-right: ${theme.spacing.sm};
  opacity: 0.6;
`;

const CustomOption = styled.div`
  font-style: italic;
  opacity: 0.8;
`;

export const SuggestionDropdown: React.FC<Props> = ({ 
  suggestions, 
  onSelect, 
  currentValue,
  showRemoveOption = false
}) => {
  return (
    <Dropdown
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.15 }}
    >
      {/* Add Remove option at the top only if this blank is removable */}
      {showRemoveOption && (
        <SuggestionItem
          isSelected={false}
          isCustom={false}
          isRemove={true}
          onClick={() => onSelect('__REMOVE_BLANK__')}
          type="button"
        >
          <SuggestionIcon>🗑️</SuggestionIcon>
          Remove this blank
        </SuggestionItem>
      )}
      
      {suggestions.map((suggestion, index) => {
        const isCustom = suggestion === 'Custom...';
        const isSelected = !isCustom && suggestion === currentValue;
        
        return (
          <SuggestionItem
            key={index}
            isSelected={isSelected}
            isCustom={isCustom}
            onClick={() => onSelect(suggestion)}
            type="button"
          >
            {isCustom ? (
              <CustomOption>
                <SuggestionIcon>✏️</SuggestionIcon>
                {suggestion}
              </CustomOption>
            ) : (
              <>
                {isSelected && <SuggestionIcon>✓</SuggestionIcon>}
                {suggestion}
              </>
            )}
          </SuggestionItem>
        );
      })}
    </Dropdown>
  );
};